import { Box, Button, Divider, Link as MuiLink, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { SectionCard } from "@/components/DesignSystem";
import { colors, riskColors } from "@/theme/eventsTokens.ts";
import {
  HOSTILE_SECOND_FACTOR_FILTER,
  TRUSTED_SECOND_FACTOR_FILTER,
  ipActivityLink,
} from "@/utils/ipActivityLink.ts";
import type { SecondFactorBreakdown } from "@/types";

export default function SecondFactorCard({
  breakdown,
  windowDays,
}: {
  readonly breakdown: SecondFactorBreakdown;
  readonly windowDays: number;
}) {
  const { failed_hostile, failed_trusted } = breakdown;
  const none = failed_hostile === 0;
  const accent = none ? riskColors.low.main : riskColors.medium.main;
  return (
    <SectionCard title="Stopped at the second factor" sx={{ height: "100%" }}>
      <Typography
        variant="overline"
        sx={{ display: "block", color: colors.textMuted, fontWeight: 600, letterSpacing: "0.08em", mb: 1 }}
      >
        Potential first-factor compromise
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        {none ? (
          <VerifiedUserIcon sx={{ color: accent, fontSize: 32 }} />
        ) : (
          <GppMaybeIcon sx={{ color: accent, fontSize: 32 }} />
        )}
        <Typography sx={{ fontSize: "2.75rem", fontWeight: 700, lineHeight: 1, color: accent }}>
          {failed_hostile.toLocaleString()}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
        {none
          ? `No source without a successful sign-in was stopped at the second factor in the last ${windowDays} days.`
          : "sources that never sign in successfully — a correct password reached MFA but failed it, the strongest sign of stolen credentials."}
      </Typography>
      {!none && (
        <Button
          component={RouterLink}
          to={ipActivityLink(windowDays, HOSTILE_SECOND_FACTOR_FILTER)}
          variant="outlined"
          size="small"
          endIcon={<ArrowForwardIcon />}
          sx={{ mb: 2, alignSelf: "flex-start" }}
        >
          Investigate {failed_hostile.toLocaleString()} in IP Activity
        </Button>
      )}
      <ExclusionNote trusted={failed_trusted} windowDays={windowDays} />
    </SectionCard>
  );
}

// A source that also signs in normally is a real user with a device or MFA
// problem, not an attack. It is left out of the headline and said so here, with
// its own drill-in, rather than dropped without a trace.
function ExclusionNote({ trusted, windowDays }: { readonly trusted: number; readonly windowDays: number }) {
  if (trusted === 0) return null;
  return (
    <>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block" }}>
        Excluded: {trusted.toLocaleString()} sources that failed the second factor but also sign in
        normally — device or MFA problems, not attacks.{" "}
        <MuiLink component={RouterLink} to={ipActivityLink(windowDays, TRUSTED_SECOND_FACTOR_FILTER)}>
          Review in IP Activity
        </MuiLink>
      </Typography>
    </>
  );
}
